/**
 * Declaring a pipeline's parameters, and asking for their values at Run.
 *
 * A migration is run per wave, per delta, per market, and then again for the
 * rows that failed. Without parameters each of those is a copy of the pipeline,
 * and the copies drift — someone fixes a mapping in wave 3 and not in wave 2,
 * and nobody finds out until reconciliation.
 *
 * Two components, because they are two moments: `ParametersPanel` is part of
 * building the pipeline, `RunParametersModal` is part of running it.
 */

import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import type { DaqParameter } from "./graph";

export function ParametersPanel({
  parameters,
  editable,
  onChange,
}: {
  parameters: DaqParameter[];
  editable: boolean;
  onChange: (next: DaqParameter[]) => void;
}) {
  const patch = (index: number, change: Partial<DaqParameter>) =>
    onChange(parameters.map((p, i) => (i === index ? { ...p, ...change } : p)));

  return (
    <Stack gap="sm" p="md">
      <Group justify="space-between" align="center">
        <div>
          <Text fw={600}>Parameters</Text>
          <Text size="xs" c="dimmed">
            Values someone supplies when they run this pipeline. Use one anywhere in a node&apos;s
            settings as <Code>{"{{name}}"}</Code>.
          </Text>
        </div>
        <Button
          size="compact-sm"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={!editable}
          onClick={() => onChange([...parameters, { name: "", required: false }])}
        >
          Add
        </Button>
      </Group>

      {parameters.length === 0 && (
        <Alert color="gray" variant="light">
          No parameters. Add one to run the same pipeline per wave or per date range instead of
          copying it — copies drift, and the drift shows up at reconciliation.
        </Alert>
      )}

      {parameters.map((parameter, i) => (
        <Stack
          key={i}
          gap={6}
          p="xs"
          style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 8 }}
        >
          <Group gap={6} wrap="nowrap" align="flex-end">
            <TextInput
              label={i === 0 ? "Name" : undefined}
              description={i === 0 ? "Letters, digits and underscores." : undefined}
              placeholder="wave"
              value={parameter.name}
              onChange={(e) => patch(i, { name: e.currentTarget.value.trim() })}
              disabled={!editable}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label="Remove parameter"
              disabled={!editable}
              onClick={() => onChange(parameters.filter((_, j) => j !== i))}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <TextInput
            label="Label"
            placeholder={parameter.name || "Wave number"}
            value={parameter.label ?? ""}
            onChange={(e) => patch(i, { label: e.currentTarget.value })}
            disabled={!editable}
          />
          <TextInput
            label="Default"
            description="Used when a run supplies nothing."
            value={parameter.default ?? ""}
            onChange={(e) => patch(i, { default: e.currentTarget.value })}
            disabled={!editable}
          />
          <Checkbox
            label="Required"
            description="A run with no value for it is refused before anything starts."
            checked={Boolean(parameter.required)}
            onChange={(e) => patch(i, { required: e.currentTarget.checked })}
            disabled={!editable}
          />
          {parameter.name && (
            <Text size="xs" c="dimmed">
              Use it as <Code>{`{{${parameter.name}}}`}</Code>
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export function RunParametersModal({
  parameters,
  opened,
  onClose,
  onRun,
}: {
  parameters: DaqParameter[];
  opened: boolean;
  onClose: () => void;
  onRun: (values: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});

  // Re-seeded each time it opens: the defaults are what the pipeline says
  // today, and a value typed for a previous run is not a value for this one.
  useEffect(() => {
    if (!opened) return;
    setValues(
      Object.fromEntries(parameters.map((p) => [p.name, p.default ?? ""])) as Record<
        string,
        string
      >,
    );
  }, [opened, parameters]);

  const missing = parameters.filter((p) => p.required && !(values[p.name] ?? "").trim());

  return (
    <Modal opened={opened} onClose={onClose} title="Run with" centered>
      <Stack gap="sm">
        {parameters.map((parameter) => (
          <TextInput
            key={parameter.name}
            label={parameter.label || parameter.name}
            description={parameter.description || undefined}
            placeholder={parameter.default || undefined}
            withAsterisk={parameter.required}
            value={values[parameter.name] ?? ""}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [parameter.name]: e.currentTarget.value }))
            }
          />
        ))}
        <Text size="xs" c="dimmed">
          These are recorded on the run, so it stays clear afterwards which wave this was.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={missing.length > 0} onClick={() => onRun(values)}>
            Run
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
